/**
 * PWA (docs/08 §7): service worker registration (production builds only), "new version" prompt and
 * cleanup of the cached API responses.
 */
import { registerSW } from 'virtual:pwa-register';
import { useEffect, useRef } from 'react';
import { toast } from '@/components/ui/Toast';

/** Fired on `window` when a new version of the app is ready; call the `detail` function to update. */
export const PWA_NEED_REFRESH_EVENT = 'pwa:need-refresh';
export const PWA_OFFLINE_READY_EVENT = 'pwa:offline-ready';

/** Registers the Workbox service worker (production builds only). */
export function registerServiceWorker(): void {
  if (import.meta.env.DEV || !('serviceWorker' in navigator)) return;
  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      window.dispatchEvent(
        new CustomEvent(PWA_NEED_REFRESH_EVENT, { detail: () => updateSW(true) }),
      );
    },
    onOfflineReady() {
      window.dispatchEvent(new CustomEvent(PWA_OFFLINE_READY_EVENT));
    },
    onRegisteredSW(_url, registration) {
      // tablets stay open for days: look for a new version every hour
      if (registration) setInterval(() => void registration.update(), 60 * 60 * 1000);
    },
  });
}

/** Shows the "Nuova versione disponibile – Aggiorna" toast (mounted once in app.tsx). */
export function PwaUpdatePrompt() {
  const shown = useRef(false);
  useEffect(() => {
    const onNeedRefresh = (event: Event) => {
      if (shown.current) return;
      shown.current = true;
      const update = (event as CustomEvent<() => Promise<void>>).detail;
      toast({
        title: 'Nuova versione disponibile',
        description: 'Aggiorna per usare la versione più recente dell’app.',
        duration: Infinity,
        action: { label: 'Aggiorna', onClick: () => void update() },
      });
    };
    const onOfflineReady = () =>
      toast({
        title: 'App pronta anche offline',
        description: 'Le pagine già aperte restano consultabili senza rete.',
        variant: 'success',
      });
    window.addEventListener(PWA_NEED_REFRESH_EVENT, onNeedRefresh);
    window.addEventListener(PWA_OFFLINE_READY_EVENT, onOfflineReady);
    return () => {
      window.removeEventListener(PWA_NEED_REFRESH_EVENT, onNeedRefresh);
      window.removeEventListener(PWA_OFFLINE_READY_EVENT, onOfflineReady);
    };
  }, []);
  return null;
}
